import { useEffect, useRef, useState } from 'react';
import { ShieldCheck, Truck, Users } from 'lucide-react';
import { BrandLogo } from './BrandLogo';
import type { ExperimentVariant } from '../data/growthExperiments';
import { selectExperimentVariant } from '../lib/experiments';
import { trackEvent } from '../lib/analytics';

function ShopNavigation({ hidden }: { hidden: boolean }) {
  return (
    <div className="megaPanel" id="shop-navigation" hidden={hidden}>
      <div className="megaCol" role="group" aria-labelledby="shop-categories">
        <span className="megaTitle" id="shop-categories">Categorieen</span>
        <a href="/collecties/paddles/">Paddles</a>
        <a href="/collecties/ballen/">Ballen</a>
        <a href="/collecties/netten-en-belijning/">Netten &amp; belijning</a>
        <a href="/collecties/startersets/">Startersets</a>
        <a href="/collecties/tassen-accessoires/">Accessoires</a>
        <a href="/collecties/schoenen-en-bescherming/">Schoenen &amp; bescherming</a>
      </div>
      <div className="megaCol" role="group" aria-labelledby="shop-playing-style">
        <span className="megaTitle" id="shop-playing-style">Kies op speelstijl</span>
        <a href="/pickleball-paddle-controle/">Meer controle</a>
        <a href="/pickleball-paddle-power/">Meer power</a>
        <a href="/pickleball-paddle-spin/">Meer spin</a>
        <a href="/pickleball-paddle-lichtgewicht/">Lichtgewicht</a>
        <a href="/pickleball-paddle-beginners/">Voor beginners</a>
      </div>
      <div className="megaCol" role="group" aria-labelledby="shop-popular">
        <span className="megaTitle" id="shop-popular">Populair</span>
        <a href="/pickleball-starterset-kopen/">Complete startersets</a>
        <a href="/pickleball-ballen-indoor-outdoor/">Indoor vs outdoor ballen</a>
        <a href="/welke-pickleball-paddle-past-bij-mij/">Welke paddle past bij mij?</a>
        <a href="/wat-is-pickleball/">Wat is pickleball?</a>
      </div>
    </div>
  );
}

function ClubsNavigation({ hidden }: { hidden: boolean }) {
  return (
    <div className="megaPanel" id="clubs-navigation" hidden={hidden}>
      <div className="megaCol" role="group" aria-labelledby="clubs-audience">
        <span className="megaTitle" id="clubs-audience">Voor wie?</span>
        <a href="/pickleball-voor-verenigingen/">Verenigingen</a>
        <a href="/pickleball-voor-scholen/">Scholen</a>
        <a href="/pickleball-bedrijven/">Bedrijven</a>
        <a href="/pickleball-materiaal-gymles/">Gymles &amp; sportlocaties</a>
      </div>
      <div className="megaCol" role="group" aria-labelledby="clubs-solutions">
        <span className="megaTitle" id="clubs-solutions">Oplossingen</span>
        <a href="/pickleball-clubpakket/">Clubpakket &amp; advies</a>
        <a href="/pickleball-materiaal-gymles/">Schoolpakket gymles</a>
        <a href="/pickleball-clinic-materiaal/">Clinics &amp; demodagen</a>
        <a href="/collecties/netten-en-belijning/">Mobiele netten</a>
      </div>
      <div className="megaCol" role="group" aria-labelledby="clubs-why">
        <span className="megaTitle" id="clubs-why">Waarom ProPickleball</span>
        <a href="/pickleball-clubpakket/">Advies op maat</a>
        <a href="/pickleball-voor-verenigingen/">Complete pakketten</a>
        <a href="/pickleball-kopen/">Zonder fake stock/claims</a>
      </div>
    </div>
  );
}

export function Header() {
  const [heroVariant, setHeroVariant] = useState<ExperimentVariant | null>(null);
  const [openMenu, setOpenMenu] = useState<'shop' | 'clubs' | null>(null);
  const disclosureRefs = useRef<Record<'shop' | 'clubs', HTMLButtonElement | null>>({ shop: null, clubs: null });

  useEffect(() => {
    let isActive = true;
    selectExperimentVariant('home_hero_intent_v1').then((variant) => {
      if (isActive) setHeroVariant(variant);
    });
    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    if (!openMenu) return;
    const activeMenu = openMenu;

    function closeOnEscape(event: KeyboardEvent) {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      const trigger = disclosureRefs.current[activeMenu];
      setOpenMenu(null);
      requestAnimationFrame(() => trigger?.focus());
    }

    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [openMenu]);

  const headline = heroVariant?.headline ?? 'Pickleball spullen voor je eerste rally, training of clubavond.';
  const body = heroVariant?.body ?? 'Bekijk startersets, paddles, ballen, netten en accessoires uit onze eerste selectie. De verkoopbatch wordt voorbereid; je kunt producten bekijken, richtprijzen zien en een update of clubofferte aanvragen.';
  const primaryCta = heroVariant?.primaryCta ?? 'Bekijk eerste collectie';
  const primaryHref = heroVariant?.primaryHref ?? '/#live-drop';
  const secondaryCta = heroVariant?.secondaryCta ?? 'Vraag clubpakket aan';
  const secondaryHref = heroVariant?.secondaryHref ?? '/#clubs';

  return (
    <header className="hero">
      <img
        className="heroImage"
        src="/images/brand/propickleball-court-hero.webp"
        alt="Lege pickleballbaan met net, twee paddles en gele ballen"
        width="1280"
        height="853"
        loading="eager"
        fetchPriority="high"
        decoding="async"
      />
      <nav className="topbar" aria-label="Hoofdnavigatie">
        <BrandLogo />
        <div className="navlinks">
          <div className="navItem hasMega navItemShop">
            <div className="navItemTrigger">
              <a href="/producten/">Producten</a>
              <button
                ref={(element) => { disclosureRefs.current.shop = element; }}
                className="navDisclosure"
                type="button"
                aria-controls="shop-navigation"
                aria-expanded={openMenu === 'shop'}
                aria-label={openMenu === 'shop' ? 'Sluit productnavigatie' : 'Open productnavigatie'}
                onClick={() => setOpenMenu((current) => current === 'shop' ? null : 'shop')}
              >
                <span aria-hidden="true">⌄</span>
              </button>
            </div>
            <ShopNavigation hidden={openMenu !== 'shop'} />
          </div>
          <a href="/collecties/startersets/">Startersets</a>
          <div className="navItem hasMega navItemClubs">
            <div className="navItemTrigger">
              <a href="/pickleball-clubpakket/">Clubs &amp; scholen</a>
              <button
                ref={(element) => { disclosureRefs.current.clubs = element; }}
                className="navDisclosure"
                type="button"
                aria-controls="clubs-navigation"
                aria-expanded={openMenu === 'clubs'}
                aria-label={openMenu === 'clubs' ? 'Sluit Clubs en scholen-navigatie' : 'Open Clubs en scholen-navigatie'}
                onClick={() => setOpenMenu((current) => current === 'clubs' ? null : 'clubs')}
              >
                <span aria-hidden="true">⌄</span>
              </button>
            </div>
            <ClubsNavigation hidden={openMenu !== 'clubs'} />
          </div>
          <a href="/wat-is-pickleball/">Leren</a>
          <a
            className="navCta"
            href="/keuzehulp/"
            onClick={() => trackEvent({ event: 'cta_click', location: 'nav', label: 'Paddle keuzehulp', routeFamily: 'home', placement: 'nav_cta' })}
          >
            Paddle keuzehulp
          </a>
        </div>
      </nav>
      <section className="heroContent" id="top">
        <div className="heroCopy">
          <p className="eyebrow">Pickleball webshop voor Nederland & Belgie</p>
          <p className="prelaunchBadge" role="status">Prelaunch: eerst kiezen en advies, bestellen volgt pas na gecontroleerde product- en leveringsinformatie.</p>
          <h1>{headline}</h1>
          <p className="lead">{body}</p>
          <div className="ctaRow">
            <a className="primaryCta" href={primaryHref} onClick={() => trackEvent({ event: 'cta_click', location: 'hero', label: primaryCta, routeFamily: 'home', placement: heroVariant?.id ?? 'control' })}>{primaryCta}</a>
            <a className="secondaryCta" href={secondaryHref} onClick={() => trackEvent({ event: 'cta_click', location: 'hero', label: secondaryCta, routeFamily: 'home', placement: heroVariant?.id ?? 'control' })}>{secondaryCta}</a>
          </div>
        </div>
        <div className="promiseStrip" aria-label="Belofte van ProPickleball.nl">
          <div><ShieldCheck /> Startersets, paddles en ballen op een plek</div>
          <div><Truck /> NL/BE service en retourinformatie</div>
          <div><Users /> Clubpakketten op aanvraag</div>
        </div>
      </section>
    </header>
  );
}
